<template>
  <section class="notifications-panel">
    <header class="notifications-panel__header">
      <h2 class="notifications-panel__title">Notifications</h2>
      <v-btn
        v-if="notificationsStore.unreadCount"
        variant="text"
        size="x-small"
        color="primary"
        @click="notificationsStore.markAllRead()"
      >
        Tout marquer lu
      </v-btn>
    </header>

    <v-tabs
      :model-value="tabIndex"
      color="primary"
      density="compact"
      grow
      @update:model-value="onTab"
    >
      <v-tab v-for="(tab, index) in tabs" :key="tab.key" :value="index">
        <v-badge
          :content="unread[tab.key]"
          :model-value="unread[tab.key] > 0"
          color="info"
        >
          {{ tab.label }}
        </v-badge>
      </v-tab>
    </v-tabs>
    <div v-if="notificationsStore.loadStatus === 'loading'" class="notifications-panel__empty">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <div
      v-else-if="!notificationsStore.filtered.length"
      class="notifications-panel__empty"
    >
      Aucune notification
    </div>

    <div v-else class="notifications-list">
      <template v-for="section in sections">
        <p
          v-if="section.items.length"
          :key="`${section.key}-label`"
          class="notifications-list__label"
        >
          {{ section.label }}
        </p>
        <template v-if="section.items.length">
          <div
            v-for="item in section.items"
            :key="item.storeKey || item.id"
            class="notification-item"
            :class="{ 'notification-item--unread': !item.read }"
            role="button"
            tabindex="0"
            @click="openItem(item)"
            @keydown.enter="openItem(item)"
          >
            <v-tooltip location="start" color="primary">
              <template v-slot:activator="{ props }">
                <div class="notification-avatar" v-bind="props">
                  <v-avatar
                    v-if="entityMeta(item)"
                    size="36"
                    :color="entityMeta(item).tone"
                    variant="tonal"
                  >
                    <v-icon :icon="entityMeta(item).icon" size="20" />
                  </v-avatar>
                  <v-avatar v-else size="36" class="notification-avatar__brand">
                    <img
                      :src="$appConfig.appLogoUrl"
                      alt="Katana Intelligence"
                      class="notification-avatar__logo"
                    >
                  </v-avatar>
                  <v-avatar
                    v-if="entityMeta(item)"
                    size="18"
                    :color="entityMeta(item).tone"
                    class="notification-avatar__actor"
                  >
                    <v-icon :icon="entityMeta(item).statusIcon" size="12" />
                  </v-avatar>
                  <v-avatar
                    v-else-if="hasHumanActor(item)"
                    size="20"
                    color="info"
                    class="notification-avatar__actor"
                  >
                    <img
                      v-if="item.actor.avatar"
                      :src="$functions.getImgLink(item.actor.avatar, 'small')"
                      :alt="actorLabel(item)"
                    >
                    <span v-else>{{ item.actorInitials }}</span>
                  </v-avatar>
                </div>
              </template>
              <span>{{ actorLabel(item) }}</span>
            </v-tooltip>

            <div class="notification-item__body">
              <div class="notification-item__head">
                <span class="notification-item__title">{{ item.title }}</span>
                <span class="notification-item__time">{{ relative(item.createdAt) }}</span>
              </div>
              <div v-if="entityMeta(item)" class="notification-item__sub">
                <span class="notification-item__ref">{{ entityMeta(item).heading }}</span>
                <span v-if="entityMeta(item).party" class="notification-item__meta">
                  · {{ entityMeta(item).party }}
                </span>
                <span v-if="entityMeta(item).amount" class="notification-item__meta">
                  · {{ entityMeta(item).amount }}
                </span>
              </div>
              <div v-else-if="item.entity" class="notification-item__sub">
                {{ item.entity.label }}
                <span v-if="item.entity.status" class="notification-item__meta">
                  · {{ item.entity.status }}
                </span>
              </div>
              <div
                v-else-if="item.body"
                class="notification-item__sub notification-body"
                v-html="safeBody(item.body)"
              />
            </div>

            <div class="notification-actions">
              <v-btn
                v-if="item.link"
                icon="mdi-open-in-new"
                variant="text"
                size="24"
                density="compact"
                :aria-label="item.link"
                @click.stop="openLink(item)"
              />
              <v-btn
                v-if="!item.read"
                icon="mdi-check"
                variant="text"
                size="24"
                density="compact"
                aria-label="Marquer comme lu"
                @click.stop="markRead(item)"
              />
              <v-btn
                v-if="adminAccess"
                class="notification-actions__delete"
                icon="mdi-delete-outline"
                variant="text"
                size="24"
                density="compact"
                aria-label="Supprimer"
                @click.stop="removeItem(item)"
              />
            </div>
          </div>
        </template>
      </template>

      <div v-if="notificationsStore.hasMoreRead" class="pa-4 text-center">
        <v-btn
          variant="text"
          size="small"
          color="primary"
          @click="notificationsStore.loadMoreRead()"
        >
          Charger plus
        </v-btn>
      </div>
    </div>
  </section>
</template>

<script>
import { setSnackbar } from "@/stores/ui";
import { mapStores } from "pinia";
import { useNotificationsStore } from "@/stores/notifications";
import { sanitizeNotificationHtml } from "@/utils/sanitizeNotificationHtml";
import { useModuleItems } from "@/composables/useModuleItems";
import { money, partyName } from "@/modules/format";
import { toMs } from "@/utils/time";

const ENTITY_TYPES = {
  quote: { noun: "Devis", icon: "mdi-file-document-edit-outline" },
  invoice: { noun: "Facture", icon: "mdi-receipt-text-outline" },
};

const ENTITY_STATUSES = {
  accepté: { tone: "success", icon: "mdi-check" },
  refusé: { tone: "error", icon: "mdi-close" },
};

const ENTITY_TONES = {
  success: { tone: "success", icon: "mdi-check" },
  error: { tone: "error", icon: "mdi-close" },
  warning: { tone: "warning", icon: "mdi-cancel" },
  info: { tone: "info", icon: "mdi-information-variant" },
};

const FALLBACK_STATUS = { tone: "primary", icon: "mdi-information-variant" };

const TABS = [
  { key: "company", label: "Entreprise" },
  { key: "account", label: "Compte" },
  { key: "team", label: "Katana" },
];

const TAB_CHANNELS = TABS.map(tab => tab.key);

export default {
  name: "NotificationsPanel",

  props: {
    adminAccess: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const { items: quotes } = useModuleItems("quotes", { party: "customer" });
    const { items: invoices } = useModuleItems("invoices", { party: "customer" });
    return { quotes, invoices };
  },

  computed: {
    ...mapStores(useNotificationsStore),

    documentsById() {
      return {
        quote: new Map(this.quotes.map(doc => [doc.id, doc])),
        invoice: new Map(this.invoices.map(doc => [doc.id, doc])),
      };
    },

    tabs() {
      return TABS;
    },

    tabIndex() {
      return Math.max(0, TAB_CHANNELS.indexOf(this.notificationsStore.channel));
    },

    unread() {
      return this.notificationsStore.unreadByChannel;
    },

    sections() {
      return [
        { key: "today", label: "Aujourd'hui", items: this.notificationsStore.grouped.today },
        { key: "earlier", label: "Plus tôt", items: this.notificationsStore.grouped.earlier },
      ];
    },
  },

  methods: {
    onTab(index) {
      this.notificationsStore.setChannel(TAB_CHANNELS[index] || "company");
    },

    entityMeta(item) {
      const entity = item?.entity;
      const type = ENTITY_TYPES[entity?.type];
      if (!type) return null;

      const doc = this.documentsById[entity.type].get(entity.id);
      const number = doc?.[entity.type === "quote" ? "nQuote" : "nInvoice"]
        ?? (entity.label && entity.label !== entity.id ? entity.label : "");
      const status = ENTITY_TONES[entity.tone]
        || ENTITY_STATUSES[String(entity.status || "").toLowerCase()]
        || FALLBACK_STATUS;

      return {
        icon: type.icon,
        tone: status.tone,
        statusIcon: status.icon,
        heading: number !== "" ? `${type.noun} n°${number}` : type.noun,
        party: (doc ? partyName(doc.customerDetails) : "") || entity.party || "",
        amount: doc && Number(doc.value) ? money(Number(doc.value)) : "",
      };
    },

    hasHumanActor(item) {
      const actor = item?.actor;
      if (!actor) return false;
      if (actor.system === true) return false;
      if (!actor.id) return false;
      const name = String(actor.displayName || "").trim().toLowerCase();
      return name && name !== "katana" && name !== "katana intelligence";
    },

    actorLabel(item) {
      if (this.hasHumanActor(item)) {
        const name = String(item.actor.displayName || "").trim();
        return `${name} · Katana Intelligence`;
      }
      return "Katana Intelligence";
    },

    safeBody(body) {
      return sanitizeNotificationHtml(body);
    },

    relative(value) {
      const ms = toMs(value);
      if (!ms) return "";
      const delta = Date.now() - ms;
      if (delta < 60000) return "à l'instant";
      if (delta < 3600000) return `il y a ${Math.floor(delta / 60000)} min`;
      if (delta < 86400000) return `il y a ${Math.floor(delta / 3600000)} h`;
      return this.$functions.getDateFormat(value);
    },

    async markRead(item) {
      if (!item.read) await this.notificationsStore.markRead(item.storeKey || item.id);
    },

    async removeItem(item) {
      if (!this.adminAccess) return;
      try {
        await this.notificationsStore.remove(item.storeKey || item.id);
      } catch (error) {
        console.error(error);
        setSnackbar({
          text: "Impossible de supprimer la notification",
          color: "error",
        });
      }
    },

    openLink(item) {
      if (!item.link) return;
      if (/^https?:\/\//i.test(item.link)) {
        window.open(item.link, "_blank", "noopener,noreferrer");
        return;
      }
      this.$router.push(item.link).catch(() => undefined);
    },

    async openItem(item) {
      if (!item.read) await this.notificationsStore.markRead(item.storeKey || item.id);
    },
  },
};
</script>

<style scoped>
.notifications-panel {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

.notifications-panel > .v-tabs {
  flex: none;
}

.notifications-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px 4px;
}

.notifications-panel__title {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  opacity: 0.6;
}

.notifications-panel__empty {
  padding: 32px 16px;
  font-size: 13px;
  text-align: center;
  opacity: 0.6;
}

.notifications-list {
  flex: 1;
  min-height: 0;
  padding: 0 8px 8px;
  overflow-y: auto;
}

.notifications-list__label {
  margin: 0;
  padding: 12px 12px 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  opacity: 0.5;
}

.notification-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 8px 8px 12px;
  cursor: pointer;
  border-radius: var(--k-radius-md);
  transition: background var(--k-dur-fast);
}

.notification-item:hover {
  background: var(--k-track);
}

.notification-item--unread::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 3px;
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: rgb(var(--v-theme-primary));
  transform: translateY(-50%);
}

.notification-item__body {
  flex: 1;
  min-width: 0;
  line-height: 1.35;
}

.notification-item__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.notification-item__title {
  font-size: 13.5px;
  font-weight: 600;
}

.notification-item__time {
  flex: none;
  font-size: 11px;
  opacity: 0.5;
}

.notification-item__sub {
  font-size: 12.5px;
  opacity: 0.75;
  overflow-wrap: anywhere;
}

.notification-item__ref {
  font-weight: 500;
}

.notification-item__meta {
  opacity: 0.7;
}

.notification-actions {
  position: absolute;
  right: 6px;
  bottom: 6px;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  background: rgb(var(--v-theme-surface));
  border-radius: var(--k-radius-md);
  box-shadow: 0 0 0 1px var(--k-track);
  opacity: 0;
  pointer-events: none;
  transition: opacity var(--k-dur-fast);
}

.notification-actions .v-btn {
  opacity: 0.6;
}

.notification-actions .v-btn:hover {
  opacity: 1;
}

.notification-actions__delete:hover {
  color: rgb(var(--v-theme-error));
}

.notification-item:hover .notification-actions,
.notification-item:focus-within .notification-actions {
  opacity: 1;
  pointer-events: auto;
}

.notification-body {
  max-height: 4.1em;
  overflow: hidden;
}

.notification-body :deep(p) {
  margin: 0 0 4px;
}

.notification-body :deep(a) {
  color: inherit;
  text-decoration: underline;
}

.notification-avatar {
  position: relative;
  flex: none;
  width: 36px;
  height: 36px;
}

.notification-avatar__brand {
  background: var(--k-track);
}

.notification-avatar__logo {
  width: 70%;
  height: 70%;
  object-fit: contain;
}

.notification-avatar__actor img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.notification-avatar__actor {
  position: absolute;
  right: -3px;
  bottom: -3px;
  z-index: 1;
  border: 2px solid rgb(var(--v-theme-surface));
  font-size: 8px;
  color: #fff;
}

@media (hover: none) {
  .notification-actions {
    opacity: 1;
    pointer-events: auto;
  }
}
</style>
